import { durationLabel } from '@ghp/domain'
import { MapPin, Users } from 'lucide-react'

import type { CachedInternalMeeting } from '../../../offline/data-db'
import { fillsDays, placementStyle, type SlotPlacement } from '../lib/agenda-layout'
import s from './InternalMeetingCard.module.css'

/**
 * Un **point interne** de l'équipe sur la gouttière (#319) — le créneau « BUREAU » que GHP DIGIT
 * réservait dans l'agenda de l'agent : l'heure et la durée, l'intitulé, le lieu.
 *
 * ⚠️ **Une `<article>`, pas un `<button>`** : il n'y a rien à ouvrir. Un point interne n'a ni
 * demande, ni pré-fiche, ni saisie, et le terrain ne modifie pas le planning (D4). Une carte qui
 * réagirait au doigt sans rien ouvrir se lirait comme une panne.
 *
 * ⚠️ **Aucune maquette terrain ne le dessine** : la teinte et l'icône sont celles du point interne
 * de la grille du bureau (`.event[data-kind='internal']`, prototype back-office validé), pour que
 * l'équipe et la planificatrice reconnaissent le même créneau sous la même couleur.
 */
export interface InternalMeetingCardProps {
  meeting: CachedInternalMeeting
  placement: SlotPlacement
  /** Le point interne est fini (`isDone`, #577) : la carte s'estompe. */
  done: boolean
}

export function InternalMeetingCard({ meeting, placement, done }: InternalMeetingCardProps) {
  return (
    <article
      className={s.card}
      style={placementStyle(placement)}
      data-done={done ? '' : undefined}
      // Le nom dit la nature du créneau, que seules la teinte et l'icône portent à l'écran.
      aria-label={`Point interne : ${meeting.title}`}
    >
      <span className={s.head}>
        <Users aria-hidden />
        {/* La durée à côté de l'heure, comme la grille du bureau : ce qui dit à l'équipe quand
            elle sera de nouveau libre. */}
        <span className={s.time}>{fillsDays(meeting) ? 'Journée entière' : placement.time}</span> ·{' '}
        {durationLabel(meeting.durationMinutes)}
      </span>
      <span className={s.title}>{meeting.title}</span>
      <span className={s.place}>
        <MapPin aria-hidden />
        {meeting.place ?? 'Lieu non précisé'}
      </span>
    </article>
  )
}
